// apps/web/src/components/OtpInput.tsx
//
// 6-digit OTP input (phase-1-5 §4). Each box has aria-label
// "Digit N of 6"; the group has a visible label passed via `legend`.
// Supports:
//   - auto-advance on digit entry
//   - backspace to move to previous box
//   - paste of a full code (or with junk mixed in)
//   - autocomplete="one-time-code" so iOS/Android autofill works
//   - auto-submit (via onComplete) once all 6 are filled
//   - explicit submit button for keyboard / screen-reader users

import { useEffect, useId, useRef } from 'react';
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react';

const OTP_LENGTH = 6;

export interface OtpInputProps {
  value: string;
  onChange: (next: string) => void;
  onComplete?: (code: string) => void;
  legend?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  hasError?: boolean;
}

function onlyDigits(s: string): string {
  return s.replace(/\D/g, '');
}

export function OtpInput({
  value,
  onChange,
  onComplete,
  legend = 'Enter the 6-digit code',
  disabled = false,
  autoFocus = true,
  hasError = false,
}: OtpInputProps): JSX.Element {
  const groupId = useId();
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  // Split value into per-box digits; pad to OTP_LENGTH.
  const digits = value.padEnd(OTP_LENGTH, ' ').split('').slice(0, OTP_LENGTH);

  // Autofocus the first box on mount (skipped on error so the
  // user re-reads the error first).
  useEffect(() => {
    if (autoFocus && !hasError && !disabled) {
      refs.current[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setDigitAt(index: number, ch: string): void {
    const arr = digits.slice();
    arr[index] = ch;
    const next = arr.join('').replace(/ /g, '');
    onChange(next);
    if (next.length === OTP_LENGTH) onComplete?.(next);
  }

  function handleChange(index: number, e: ChangeEvent<HTMLInputElement>): void {
    const ch = onlyDigits(e.target.value).slice(-1);
    if (ch.length === 0) {
      // Clear this box.
      const arr = digits.slice();
      arr[index] = '';
      onChange(arr.join('').replace(/ /g, ''));
      return;
    }
    setDigitAt(index, ch);
    // Move to next box.
    refs.current[index + 1]?.focus();
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Backspace') {
      if (digits[index] && digits[index] !== ' ') {
        const arr = digits.slice();
        arr[index] = '';
        onChange(arr.join('').replace(/ /g, ''));
      } else {
        // Move to previous box and clear it.
        const prev = Math.max(0, index - 1);
        const arr = digits.slice();
        arr[prev] = '';
        onChange(arr.join('').replace(/ /g, ''));
        refs.current[prev]?.focus();
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft') {
      refs.current[Math.max(0, index - 1)]?.focus();
    } else if (e.key === 'ArrowRight') {
      refs.current[Math.min(OTP_LENGTH - 1, index + 1)]?.focus();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>): void {
    e.preventDefault();
    const text = onlyDigits(e.clipboardData.getData('text'));
    if (text.length === 0) return;
    // Take the first OTP_LENGTH digits; fill from box 0.
    const taken = text.slice(0, OTP_LENGTH);
    const arr = digits.slice();
    for (let i = 0; i < OTP_LENGTH; i += 1) {
      arr[i] = taken[i] ?? ' ';
    }
    const next = arr.join('').replace(/ /g, '');
    onChange(next);
    const lastFilled = Math.min(OTP_LENGTH - 1, taken.length - 1);
    refs.current[lastFilled]?.focus();
    if (next.length === OTP_LENGTH) onComplete?.(next);
  }

  return (
    <fieldset
      aria-describedby={`${groupId}-help`}
      aria-invalid={hasError || undefined}
      className="block"
    >
      <legend className="block text-sm font-medium text-gray-800 dark:text-gray-200">
        {legend}
      </legend>
      <div
        className="mt-2 flex gap-2"
        role="group"
        aria-label={`${OTP_LENGTH}-digit code input`}
      >
        {Array.from({ length: OTP_LENGTH }).map((_, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            pattern="[0-9]*"
            maxLength={1}
            disabled={disabled}
            value={digits[i] === ' ' ? '' : digits[i]}
            aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className={[
              'h-12 w-10 rounded border text-center text-lg font-mono',
              'bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100',
              hasError
                ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                : 'border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-gray-700',
              'focus:outline-none',
              'disabled:bg-gray-50 disabled:text-gray-400',
            ].join(' ')}
          />
        ))}
      </div>
      <p id={`${groupId}-help`} className="sr-only">
        Type the 6-digit code. The input auto-advances on each digit.
      </p>
    </fieldset>
  );
}
